import React, { useState, useMemo } from 'react'
import { Model } from '../../../types/models/model'
import { useFetchModelsQuery, useCreateModelMutation } from '../../../services/modelsApi'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useNavigate } from 'react-router-dom'
import { useGetProjectQuery } from '../../../services/projectsApi'
import * as S from './styles'
import {
  selectSelectedModelId,
  selectCompareModelId,
  setSelectedModelId,
  setCompareModelId,
} from '../../../store/slices/modelSlice'
import SelectModelTreeModal from '../../modals/SelectModelTreeModal/SelectModelTreeModal'
import ErrorDialog from '../../modals/ErrorDialog'
import NoModelsCard from '../ModelsOverview/components/NoModelsCard'
import NewModelModal from '../../modals/NewModelModal'
import ModelComparisonCore from './ModelComparisonCore'

// Helper function to extract error messages safely
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    typeof (error as { data?: { message?: string } }).data === 'object' &&
    (error as { data?: { message?: string } }).data?.message
  ) {
    return (error as { data: { message: string } }).data.message
  }
  return fallback
}

const ModelComparisonView: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId) || null
  const compareModelId = useAppSelector(selectCompareModelId)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  // Get child models (fine-tuned iterations) and sort by creation date
  const childModels = useMemo(() => {
    const children = modelsData?.models?.filter((m) => m.parent_id === selectedModelId) || []
    return [...children].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  }, [modelsData?.models, selectedModelId])

  // Get the base model (parent) and the model to compare (either selected child or latest child)
  const baseModel = modelsData?.models?.find((m) => m.id === selectedModelId)
  const latestChildModel = childModels[0]
  const compareModel = compareModelId
    ? modelsData?.models?.find((m) => m.id === compareModelId)
    : latestChildModel
  const [activeTab] = useState<'performance' | 'realtime' | 'dataset'>('performance')
  const [showSelectModal, setShowSelectModal] = useState(false)

  // Inference handled in ModelComparisonCore
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const [showNewModelModal, setShowNewModelModal] = useState(false)
  const [selectedBaseModel, setSelectedBaseModel] = useState<{
    id: string
    name: string
  } | null>(null)
  const [filterText, setFilterText] = useState('')

  const [createModel] = useCreateModelMutation()

  const handleModelSelect = (model: Model) => {
    dispatch(setCompareModelId(model.id))
    setShowSelectModal(false)
  }

  // Use the baseModel and compareModel instead of model1/model2
  const model1 = baseModel
  const model2 = compareModel

  // presentation details moved to ModelComparisonCore

  // Retained for compatibility with child components but unused here
  const getMetricProgressData = (_metricKey: string) => {
    const getEntries = (model: Model | undefined) => {
      if (!model?.training_stats) return []

      const trainingStats =
        project?.annotation_type === 'bounding_box' ||
        project?.annotation_type === 'oriented_bounding_box'
          ? model.training_stats.bounding_box
          : model.training_stats.classification

      if (!trainingStats?.training_job_metrics) return []

      const metricsMap = trainingStats.training_job_metrics as Record<
        string,
        { training_job_entries: unknown[] }
      >
      const preferredKey =
        trainingStats.training_job_name && metricsMap[trainingStats.training_job_name]
          ? trainingStats.training_job_name
          : undefined
      const fallbackKey = !preferredKey
        ? Object.keys(metricsMap).sort(
            (a, b) =>
              (metricsMap[b]?.training_job_entries?.length || 0) -
              (metricsMap[a]?.training_job_entries?.length || 0)
          )[0]
        : undefined
      const resolvedKey = preferredKey || fallbackKey
      if (!resolvedKey) return []
      const jobEntries = metricsMap[resolvedKey]?.training_job_entries || []
      return jobEntries
    }

    const model1Entries = getEntries(model1)
    const model2Entries = getEntries(model2)

    const getMetricValue = (entry: unknown) => {
      if (
        project?.annotation_type === 'bounding_box' ||
        project?.annotation_type === 'oriented_bounding_box'
      ) {
        const bboxEntry = entry as {
          metrics_map_50_95?: number | null
          metrics_precision?: number | null
          metrics_recall?: number | null
        }
        switch (_metricKey) {
          case 'mAP':
            return bboxEntry.metrics_map_50_95 ? bboxEntry.metrics_map_50_95 * 100 : 0
          case 'precision':
            return bboxEntry.metrics_precision ? bboxEntry.metrics_precision * 100 : 0
          case 'recall':
            return bboxEntry.metrics_recall ? bboxEntry.metrics_recall * 100 : 0
          case 'f1':
            if (bboxEntry.metrics_precision && bboxEntry.metrics_recall) {
              return (
                ((2 * bboxEntry.metrics_precision * bboxEntry.metrics_recall) /
                  (bboxEntry.metrics_precision + bboxEntry.metrics_recall) || 0) * 100
              )
            }
            return 0
          default:
            return 0
        }
      } else {
        const classEntry = entry as { accuracy?: number | null }
        switch (_metricKey) {
          case 'accuracy':
            return classEntry.accuracy ? classEntry.accuracy * 100 : 0
          default:
            return 0
        }
      }
    }

    return {
      labels: Array.from(
        { length: Math.max(model1Entries.length, model2Entries.length) },
        (_, i) => `Epoch ${i + 1}`
      ),
      datasets: [
        {
          label: model1?.name || 'Model 1',
          data: model1Entries.map((entry) => getMetricValue(entry)),
          borderColor: 'rgba(13, 202, 240, 1)',
          backgroundColor: 'rgba(13, 202, 240, 0.2)',
          tension: 0.4,
        },
        {
          label: model2?.name || 'Model 2',
          data: model2Entries.map((entry) => getMetricValue(entry)),
          borderColor: 'rgba(25, 135, 84, 1)',
          backgroundColor: 'rgba(25, 135, 84, 0.2)',
          tension: 0.4,
        },
      ],
    }
  }

  // inference handled in ModelComparisonCore

  // deployment gating moved to ModelComparisonCore

  // local UI state moved to ModelComparisonCore

  const handleCreateModel = () => {
    setShowNewModelModal(true)
  }

  const generateRandomName = () => {
    const adjectives = ['Agile', 'Smart', 'Quick', 'Bright', 'Sharp']
    const nouns = ['Vision', 'Eye', 'Sight', 'View', 'Lens']
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    return `${randomAdjective} ${randomNoun}`
  }

  const handleGenerateNewModel = async () => {
    if (selectedBaseModel && selectedProjectId) {
      try {
        const newModelName = generateRandomName()
        const newModel = await createModel({
          project_id: selectedProjectId,
          name: newModelName,
          description: '',
        }).unwrap()

        // Wait for a brief moment to allow the cache to update
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Set the selected model ID
        dispatch(
          setSelectedModelId({
            modelId: newModel.id,
            projectId: selectedProjectId || '',
          })
        )

        // Navigate to the training subview
        navigate(`/models/${selectedProjectId}/training`)

        // Close the modal
        setShowNewModelModal(false)
        setSelectedBaseModel(null)
        setFilterText('')
      } catch (err: unknown) {
        const message = getErrorMessage(err, 'Failed to create new model')
        setErrorMessage(message)
        setShowError(true)
      }
    }
  }

  return (
    <S.ViewContainer>
      {!modelsData?.models || modelsData?.models.length === 0 ? (
        <>
          <NoModelsCard onCreateModel={handleCreateModel} />
          <NewModelModal
            show={showNewModelModal}
            onClose={() => {
              setShowNewModelModal(false)
              setSelectedBaseModel(null)
              setFilterText('')
            }}
            baseModels={[
              {
                id: 'baseModel1',
                name: 'AG Base',
                image: '/agileview-eye.png',
              },
            ]}
            filterText={filterText}
            setFilterText={setFilterText}
            selectedBaseModel={selectedBaseModel}
            onBaseModelSelect={setSelectedBaseModel}
            onGenerateNewModel={handleGenerateNewModel}
          />
        </>
      ) : (
        <div className="d-flex flex-column h-100">
          {/* Tab Navigation and Content */}
          <ModelComparisonCore
            project={project}
            model1={model1}
            model2={model2}
            initialTab={activeTab}
            onRequestSelectModel2={() => setShowSelectModal(true)}
            onError={(msg) => {
              setErrorMessage(msg)
              setShowError(true)
            }}
          />
        </div>
      )}

      <SelectModelTreeModal
        show={showSelectModal}
        onHide={() => setShowSelectModal(false)}
        onModelSelect={handleModelSelect}
        selectedModelId={compareModelId || null}
        selectionMode="compare"
        excludeModelIds={selectedModelId ? [selectedModelId] : []}
      />

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage || ''}
      />
    </S.ViewContainer>
  )
}

export default ModelComparisonView
